const DEFAULT_PROP_VALUE = ''

/**
 * @private
 * @param {RegExp} regexp - Regular expression object
 * @param {string} subject - The search string to be found
 * @returns {boolean} Whether the needle has been found or not
 */
function objectContainsTextInternal(regexp, subject) {
    return regexp.test(subject)
}

/**
 * Escaping special chars in search string to be able to search for
 * them without errors.
 *
 * @private
 * @see {@link http://stackoverflow.com/questions/3446170/escape-string-for-use-in-javascript-regex/6969486#6969486}
 * @param {string} searchString - The search string to be found
 * @returns {string} The escaped search string
 */
function escapeRegExp(searchString) {
    return searchString.replace(/[-[\]/{}()*+?.\\^$|]/g, '\\$&')
}

/**
 * @TODO: [ATL-3192] Check if the RegExp could be replaced by something simpler
 *
 * @private
 * @param {string|RegExp} searchString - The search string to be found or regular expression objecr
 * @param {boolean} [caseInsensitive=true] - Flag to set case insensitive search to true
 * @returns {RegExp} The ready to use regexp object
 */
function createRegExp(searchString, caseInsensitive = true) {
    if (searchString instanceof RegExp) {
        return searchString
    }

    searchString = searchString.trim()
    if (caseInsensitive) {
        searchString = searchString.toLowerCase()
    }

    return new RegExp(escapeRegExp(searchString), caseInsensitive ? 'i' : '')
}

/**
 * Stringifies an object with help of the given prop names
 * (converts the values belonging to the specific keys of the object).
 *
 * @private
 * @param {Object} object
 * @param {Array} props
 * @returns {string}
 */
function createSubject(object, props) {
    return props
        .reduce((l, prop) => {
            // Split prop if slashes are included
            const propParts = prop.split('/')
            // Found a slash in the prop definition
            if (propParts.length > 1) {
                let result = object
                let propPart = ''
                // Recursively add the specified object values
                for (propPart of propParts) {
                    if (result) {
                        result = result[propPart] || DEFAULT_PROP_VALUE
                    } else {
                        break
                    }
                }
                // If object is left get all values
                // If array of simple type is left, use it without changes
                if (typeof result === 'object') {
                    result = Object.values(result)
                }
                l.push(String(result))
            } else {
                l.push(object[prop] || DEFAULT_PROP_VALUE)
            }

            return l
        }, [])
        .join(' ')
}

/**
 * @private
 * @param {RegExp} regexp
 * @param {Array} properties
 * @returns {Function}
 */
function createFilterCallback(regexp, properties) {
    return object => objectContainsTextInternal(regexp, createSubject(object, properties))
}

/**
 * Filters a list of objects by the given text search through the given properties.
 * The properties-parameter is an array containing strings - these can either be simple strings
 * (which means direct key access) or strings with slashes / inside to go deeper inside the object structure.
 * Example: properties = ['name', 'number', 'driver/driverName', 'vehicle/additionalInfo/text']
 *
 * @template T
 * @param {T[]} objects - The list of objects to search through
 * @param {string} text - The text to search for
 * @param {string[]} properties - The list of properties to search of the given objects
 * @param {boolean} [caseInsensitive=true] - True if the search should be case insensitive (default), false if not
 * @returns {T[]} The filtered list or an empty list if no match was found
 */
function filterObjectsByText(objects, text, properties, caseInsensitive = true) {
    const matchRegex = createRegExp(text, caseInsensitive)

    return objects.filter(createFilterCallback(matchRegex, properties))
}

/**
 * Filters a list of objects by the given text search through the given properties.
 * The properties-parameter is an array containing strings - these can either be simple strings
 * (which means direct key access) or strings with slashes / inside to go deeper inside the object structure.
 * Example: properties = ['name', 'number', 'driver/driverName', 'vehicle/additionalInfo/text']
 * This method returns the items sorted by the index of the matched regexp - the lower
 * the index, the higher the object score.
 *
 * @template T
 * @param {T[]} objects - The list of objects to search through
 * @param {string} text - The text to search for
 * @param {string[]} properties - The list of properties to search of the given objects
 * @param {boolean} [caseInsensitive=true] - True if the search should be case insensitive (default), false if not
 * @param {Function} [compareStrings] - Function for custom sorting of same-score-results
 * @returns {T[]} The filtered list sorted by matching index score or an empty list if no match was found
 */
function filterObjectByTextWithWeight(objects, text, properties, caseInsensitive = true, compareStrings) {
    const wrappedFilteredResults = []
    const matchRegex = createRegExp(text, caseInsensitive)

    // Check for objects with match
    objects.forEach(object => {
        const obj = createSubject(object, properties)
        const match = obj.match(matchRegex)
        const score = match ? match.index : -1

        // Filtering the results manually is a little faster
        if (score > -1) {
            wrappedFilteredResults.push({
                object: object,
                score: score,
                stringifiedObject: obj
            })
        }
    })

    // Sort by score (index of match in stringified object)
    wrappedFilteredResults.sort((wrappedFilteredResult1, wrappedFilteredResult2) => {
        if (wrappedFilteredResult1.score === wrappedFilteredResult2.score) {
            // If score is equal, sort alphanumerical
            if (compareStrings) {
                return compareStrings(
                    wrappedFilteredResult1.stringifiedObject,
                    wrappedFilteredResult2.stringifiedObject
                )
            }
            return 0
        }
        if (wrappedFilteredResult1.score > wrappedFilteredResult2.score) {
            return 1
        }
        return -1
    })

    // Extract original object
    return wrappedFilteredResults.map(result => result.object)
}

/**
 * Checks if the given object match by the given text search through the given properties.
 *
 * @template T
 * @param {T} object - The object to search
 * @param {string} text - The text to search for
 * @param {string[]} properties - The list of properties to search of the given object
 * @param {boolean} [caseInsensitive=true] - True if the search should be case insensitive (default), false if not
 * @returns {boolean} True if the text was found in one of the properties of the given object
 */
function objectContainsText(object, text, properties, caseInsensitive = true) {
    const matchRegex = createRegExp(text, caseInsensitive)
    const subject = createSubject(object, properties)

    return objectContainsTextInternal(matchRegex, subject)
}

export { escapeRegExp, objectContainsText, filterObjectByTextWithWeight, filterObjectsByText }
